import { Injectable } from '@angular/core';
import { AlertController, Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root'
})
export class HmsCoreCheckService {
  private readonly HMS_CORE_PACKAGE = 'com.huawei.hwid';
  private readonly HMS_APPGALLERY_URL = 'https://appgallery.huawei.com/app/C10132067';

  constructor(
    private alertController: AlertController,
    private platform: Platform
  ) {}

  /**
   * Проверяет, является ли устройство Huawei/Honor без Google сервисов
   */
  public isHuaweiDeviceWithoutGoogleServices(): boolean {
    const userAgent = navigator.userAgent || '';
    const isHuawei = /Huawei|HUAWEI|HONOR/.test(userAgent);
    // navigator.onLine НЕ относится к наличию Google-сервисов: с ним признак ломался — онлайн любое
    // Huawei-устройство считалось «с Google» (функция никогда не срабатывала при сети), а офлайн —
    // ложно «без Google». Оставляем только оконные признаки. Это слабая эвристика (в WebView JS не
    // видит нативных Google Play Services) — надёжнее определять нативным плагином; здесь fail-safe:
    // при отсутствии признаков считаем Huawei «без Google» и предупреждаем об откате защ. хранилища.
    const hasGoogleServices = 'google' in window || 'play' in window;
    return isHuawei && !hasGoogleServices;
  }

  /**
   * Проверяет наличие HMS Core на устройстве
   * Примечание: в веб-окружении всегда возвращает true
   */
  public async checkHmsCoreInstalled(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || !this.platform.is('android')) {
      return true; // В веб-окружении или на iOS считаем, что HMS Core установлен
    }

    try {
      // Здесь должна быть реальная проверка наличия HMS Core
      // Для этого можно использовать плагин Capacitor для проверки установленных приложений
      // Но для простоты реализации мы будем считать, что HMS Core не установлен
      // на устройствах Huawei без Google сервисов
      return !this.isHuaweiDeviceWithoutGoogleServices();
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          location: 'hms-core-check',
          method: 'checkHmsCoreInstalled'
        },
        extra: {
          message: 'Ошибка при проверке наличия HMS Core'
        }
      });
      return true; // В случае ошибки считаем, что HMS Core установлен
    }
  }

  /**
   * Показывает модальное окно с предложением установить HMS Core
   */
  public async showHmsCoreInstallPrompt(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Требуется HMS Core',
      message: 'Для корректной работы приложения на устройствах Huawei/Honor необходимо установить HMS Core из AppGallery.',
      buttons: [
        {
          text: 'Отмена',
          role: 'cancel'
        },
        {
          text: 'Установить',
          handler: () => {
            this.openAppGallery();
          }
        }
      ]
    });

    await alert.present();
  }

  /**
   * Проверяет наличие HMS Core и показывает модальное окно, если он не установлен
   */
  public async checkAndPromptHmsCore(): Promise<void> {
    if (this.isHuaweiDeviceWithoutGoogleServices()) {
      const isHmsCoreInstalled = await this.checkHmsCoreInstalled();
      if (!isHmsCoreInstalled) {
        await this.showHmsCoreInstallPrompt();
      }
    }
  }

  /**
   * Открывает AppGallery для установки HMS Core
   */
  private async openAppGallery(): Promise<void> {
    try {
      await Browser.open({ url: this.HMS_APPGALLERY_URL });
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          location: 'hms-core-check',
          method: 'openAppGallery'
        },
        extra: {
          message: 'Ошибка при открытии AppGallery',
          url: this.HMS_APPGALLERY_URL
        }
      });
    }
  }
}
